import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  X, FolderUp, AlertCircle, CheckCircle2, Loader2,
  Folder, FileText, Image as ImageIcon, Video, Play, Trash2, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  upCategorizeBulk, upFolderCategories, upInit, upPart, upComplete,
} from "../../lib/api";

const CHUNK_SIZE = 8 * 1024 * 1024;
const PARALLEL_UPLOADS = 2;

const fmtSize = (b) => {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const VIS_LABEL = {
  sofort: "Sofort sichtbar",
  nach_NDA: "Nach NDA",
  nach_indikativ: "Nach Gebot",
  intern: "Team-intern",
};

const VIS_COLOR = {
  sofort: "bg-emerald-50 text-emerald-800",
  nach_NDA: "bg-navy/8 text-navy",
  nach_indikativ: "bg-gold/15 text-gold-dark",
  intern: "bg-rose-50 text-rose-800",
};

function fileIcon(name) {
  const n = (name || "").toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|heic|heif)$/.test(n)) return ImageIcon;
  if (/\.(mp4|mov|m4v|avi|mkv|webm)$/.test(n)) return Video;
  return FileText;
}

/**
 * FolderUploadModal — Browser-Folder-Picker mit Tree-Preview und
 * Auto-Kategorisierung.
 *
 * Phasen:
 *   "idle"        — User wählt Ordner (input.webkitdirectory)
 *   "categorize"  — Backend-Call /uploads/categorize-bulk
 *   "preview"     — Tree-Preview: Checkboxes, Kategorie-Dropdown, Visibility-Dropdown
 *   "uploading"   — Parallel-Chunked-Uploads, Progress-Bar
 *   "done"        — Zusammenfassung, Modal schließbar
 */
export default function FolderUploadModal({ open, onClose, onComplete }) {
  const [phase, setPhase] = useState("idle");
  const [items, setItems] = useState([]);          // {path, file, kategorie, visibility, is_intern, matched, folder_hint, included}
  const [categories, setCategories] = useState([]); // [{kategorie, default_visibility, ...}]
  const [collapsedFolders, setCollapsedFolders] = useState(new Set());
  const [uploadProgress, setUploadProgress] = useState({}); // {key: {pct, status}}
  const [doneCount, setDoneCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const inputRef = useRef(null);
  const cancelRef = useRef(false);

  // Reset bei Open
  useEffect(() => {
    if (!open) return;
    cancelRef.current = false;
    setPhase("idle");
    setItems([]);
    setUploadProgress({});
    setDoneCount(0);
    setFailCount(0);
    // Kategorien laden für Dropdowns
    upFolderCategories().then((c) => setCategories(c.categories || [])).catch(() => {});
  }, [open]);

  // Folder-Picker
  const pickFolder = () => inputRef.current?.click();

  const onFolderSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Build relative-paths array
    const paths = files.map((f) => f.webkitRelativePath || f.name);
    setPhase("categorize");
    try {
      const result = await upCategorizeBulk(paths);
      // Verknüpfe File-Objects mit den Kategorien
      const enriched = result.items.map((it, idx) => ({
        path: it.path,
        file: files[idx],
        kategorie: it.kategorie,
        visibility: it.default_visibility,
        is_intern: it.is_intern,
        matched: it.matched,
        folder_hint: it.folder_hint,
        included: true,
      }));
      setItems(enriched);
      setPhase("preview");
    } catch (err) {
      toast.error("Auto-Kategorisierung fehlgeschlagen");
      setPhase("idle");
    }
  };

  // Group items by Kategorie für Tree-Preview
  const grouped = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      if (!m.has(it.kategorie)) m.set(it.kategorie, []);
      m.get(it.kategorie).push(it);
    });
    return Array.from(m.entries())
      .map(([kat, list]) => ({
        kategorie: kat,
        items: list,
        included_count: list.filter((x) => x.included).length,
        total_size: list.filter((x) => x.included).reduce((s, x) => s + (x.file?.size || 0), 0),
        warning_count: list.filter((x) => !x.matched && x.included).length,
      }))
      .sort((a, b) => a.kategorie.localeCompare(b.kategorie));
  }, [items]);

  const totalSize = grouped.reduce((s, g) => s + g.total_size, 0);
  const totalIncluded = grouped.reduce((s, g) => s + g.included_count, 0);
  const totalUnmatched = items.filter((x) => !x.matched && x.included).length;

  const toggleCollapse = (kat) => {
    setCollapsedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(kat)) next.delete(kat); else next.add(kat);
      return next;
    });
  };

  const toggleAllIncluded = (kat, included) => {
    setItems((prev) => prev.map((it) =>
      it.kategorie === kat ? { ...it, included } : it
    ));
  };

  const toggleItem = (path, included) => {
    setItems((prev) => prev.map((it) => (it.path === path ? { ...it, included } : it)));
  };

  const changeKategorieAll = (oldKat, newKat) => {
    const catMeta = categories.find((c) => c.kategorie === newKat);
    setItems((prev) => prev.map((it) =>
      it.kategorie === oldKat
        ? {
            ...it,
            kategorie: newKat,
            visibility: catMeta?.default_visibility || it.visibility,
            is_intern: catMeta?.is_intern ?? it.is_intern,
            matched: true,
          }
        : it
    ));
  };

  const changeVisibilityAll = (kat, newVis) => {
    setItems((prev) => prev.map((it) =>
      it.kategorie === kat ? { ...it, visibility: newVis, is_intern: newVis === "intern" } : it
    ));
  };

  // -----------------------------------------------------------------------
  // Upload-Worker
  // -----------------------------------------------------------------------
  const uploadOne = async (item, key) => {
    try {
      setUploadProgress((p) => ({ ...p, [key]: { pct: 0, status: "uploading" } }));
      const file = item.file;
      const init = await upInit({
        filename: file.name,
        size_bytes: file.size,
        content_type: file.type || "application/octet-stream",
        kategorie: item.kategorie,
        original_pfad: item.path,
        is_intern: item.is_intern,
        visibility: item.visibility,
      });
      const totalParts = init.total_parts;
      for (let n = 1; n <= totalParts; n++) {
        if (cancelRef.current) throw new Error("Abgebrochen");
        const start = (n - 1) * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const blob = file.slice(start, end);
        let attempt = 0;
        while (true) {
          try {
            await upPart(init.upload_id, n, blob);
            break;
          } catch (err) {
            attempt++;
            if (attempt > 2) throw err;
            await new Promise((r) => setTimeout(r, 1500 * attempt));
          }
        }
        const pct = Math.round((n / totalParts) * 95);
        setUploadProgress((p) => ({ ...p, [key]: { pct, status: "uploading" } }));
      }
      setUploadProgress((p) => ({ ...p, [key]: { pct: 98, status: "assembling" } }));
      await upComplete(init.upload_id);
      setUploadProgress((p) => ({ ...p, [key]: { pct: 100, status: "done" } }));
      setDoneCount((c) => c + 1);
    } catch (err) {
      setUploadProgress((p) => ({ ...p, [key]: { pct: 0, status: "error", error: err.message } }));
      setFailCount((c) => c + 1);
    }
  };

  const startUpload = async () => {
    const queue = items.filter((it) => it.included);
    if (queue.length === 0) {
      toast.error("Keine Dateien ausgewählt");
      return;
    }
    setPhase("uploading");
    setDoneCount(0);
    setFailCount(0);
    cancelRef.current = false;

    // Parallel-Pool
    let idx = 0;
    const workers = Array.from({ length: PARALLEL_UPLOADS }, () =>
      (async () => {
        while (idx < queue.length && !cancelRef.current) {
          const myIdx = idx++;
          const item = queue[myIdx];
          await uploadOne(item, `${item.path}_${myIdx}`);
        }
      })()
    );
    await Promise.all(workers);
    setPhase("done");
    if (onComplete) onComplete();
  };

  if (!open) return null;

  return (
    <>
      <div onClick={phase === "uploading" ? undefined : onClose}
        className="fixed inset-0 bg-navy/40 z-[80]" />
      <div
        data-testid="folder-upload-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(960px,95vw)] max-h-[92vh] overflow-hidden flex flex-col bg-white border border-navy/15 z-[90] shadow-xl"
      >
        {/* Header */}
        <header className="border-b border-navy/10 px-6 py-4 flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <FolderUp size={22} className="text-gold mt-1 shrink-0" />
            <div className="min-w-0">
              <div className="pb-eyebrow">Bulk-Upload</div>
              <h3 className="font-display font-semibold text-navy text-lg sm:text-xl mt-1 tracking-tight">
                Komplette Ordnerstruktur hochladen
              </h3>
              <p className="text-xs font-sans text-navy/65 mt-1">
                Auto-Kategorisierung anhand der Top-Level-Ordner · Pfad &amp; Visibility vor Upload änderbar
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={phase === "uploading"}
            className="text-navy/55 hover:text-navy p-1 shrink-0 disabled:opacity-30"
            aria-label="Schließen"
            data-testid="folder-upload-close"
          >
            <X size={18} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {phase === "idle" && (
            <div className="flex flex-col items-center justify-center py-12 text-center gap-4" data-testid="folder-upload-idle">
              <input
                ref={inputRef}
                type="file"
                multiple
                onChange={onFolderSelected}
                className="hidden"
                data-testid="folder-upload-input"
                /* eslint-disable-next-line react/no-unknown-property */
                webkitdirectory=""
                directory=""
              />
              <Folder size={48} className="text-navy/40" />
              <div>
                <h4 className="font-display text-lg text-navy">Ordner auswählen</h4>
                <p className="text-xs font-sans text-navy/55 mt-1 max-w-md">
                  Wähle den lokalen <span className="font-medium">Newton Campus DD/</span>-Ordner.
                  Der Browser liest alle Unterordner rekursiv aus.
                </p>
              </div>
              <button
                onClick={pickFolder}
                className="pb-btn-primary text-xs uppercase tracking-widish font-semibold"
                data-testid="folder-upload-pick"
              >
                <FolderUp size={14} /> Ordner wählen
              </button>
              <p className="text-[11px] font-sans text-navy/45 max-w-md">
                Unterstützt Chrome, Edge und Safari (Desktop). Auf iPhone-Safari mobil-eingeschränkt.
                Bei &gt; 3 GB Gesamtgröße: bitte in zwei Batches.
              </p>
            </div>
          )}

          {phase === "categorize" && (
            <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="folder-upload-categorize">
              <Loader2 size={32} className="animate-spin text-gold" />
              <p className="text-sm font-sans text-navy/75">Ordnerstruktur wird analysiert…</p>
            </div>
          )}

          {(phase === "preview" || phase === "uploading" || phase === "done") && (
            <>
              {/* Summary */}
              <div className="border border-navy/12 bg-ledger px-4 py-3 mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="folder-upload-summary">
                <div className="flex gap-6 flex-wrap text-sm font-sans">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/55">Ausgewählt</div>
                    <div className="font-display text-navy text-lg tabular-nums">{totalIncluded} / {items.length}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/55">Gesamtgröße</div>
                    <div className="font-display text-navy text-lg tabular-nums">{fmtSize(totalSize)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-navy/55">Warnungen</div>
                    <div className={`font-display text-lg tabular-nums ${totalUnmatched > 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {totalUnmatched}
                    </div>
                  </div>
                  {phase !== "preview" && (
                    <>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-navy/55">Fertig</div>
                        <div className="font-display text-emerald-700 text-lg tabular-nums">{doneCount}</div>
                      </div>
                      {failCount > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-navy/55">Fehler</div>
                          <div className="font-display text-rose-700 text-lg tabular-nums">{failCount}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {phase === "preview" && (
                  <button
                    onClick={startUpload}
                    disabled={totalIncluded === 0 || totalUnmatched > 0}
                    className="pb-btn-primary text-xs uppercase tracking-widish font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                    title={totalUnmatched > 0 ? "Bitte alle Warnungen auflösen" : ""}
                    data-testid="folder-upload-start"
                  >
                    <FolderUp size={13} /> Upload starten
                  </button>
                )}
              </div>

              {totalUnmatched > 0 && phase === "preview" && (
                <div className="border-l-[3px] border-l-rose-700 bg-rose-50 px-4 py-2 mb-4 text-xs font-sans text-rose-900" data-testid="folder-upload-warning">
                  <AlertCircle size={14} className="inline mr-1" />
                  {totalUnmatched} Datei{totalUnmatched === 1 ? "" : "en"} ohne erkannte Kategorie — bitte „99_Sonstiges"-Gruppe öffnen und manuell umkategorisieren.
                </div>
              )}

              {/* Tree-Preview */}
              <ul className="space-y-2" data-testid="folder-upload-tree">
                {grouped.map((g) => {
                  const collapsed = collapsedFolders.has(g.kategorie);
                  const allIncluded = g.items.every((x) => x.included);
                  const someIncluded = g.items.some((x) => x.included);
                  return (
                    <li key={g.kategorie} className="border border-navy/12 bg-white">
                      {/* Group-Header */}
                      <div className="px-3 py-2.5 flex items-center gap-3 border-b border-navy/8" data-testid="folder-upload-group">
                        <button
                          onClick={() => toggleCollapse(g.kategorie)}
                          className="text-navy/55 hover:text-navy shrink-0"
                          title={collapsed ? "Aufklappen" : "Einklappen"}
                        >
                          {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                        </button>
                        <input
                          type="checkbox"
                          checked={allIncluded}
                          ref={(el) => { if (el) el.indeterminate = !allIncluded && someIncluded; }}
                          onChange={(e) => toggleAllIncluded(g.kategorie, e.target.checked)}
                          className="shrink-0 accent-navy"
                          disabled={phase !== "preview"}
                        />
                        <Folder size={16} className="text-gold shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display text-sm text-navy font-semibold">{g.kategorie}</span>
                            <span className="text-xs font-sans text-navy/55 tabular-nums">
                              {g.included_count} / {g.items.length} · {fmtSize(g.total_size)}
                            </span>
                            {g.warning_count > 0 && (
                              <span className="text-[10px] px-1.5 py-0.5 bg-rose-50 text-rose-800 uppercase tracking-wider">
                                {g.warning_count} unerkannt
                              </span>
                            )}
                          </div>
                        </div>
                        {phase === "preview" && (
                          <div className="flex items-center gap-1.5">
                            <select
                              value={g.kategorie}
                              onChange={(e) => changeKategorieAll(g.kategorie, e.target.value)}
                              className="px-2 py-1 text-[11px] border border-navy/15 bg-white text-navy focus:outline-none focus:border-navy font-sans"
                              data-testid="folder-upload-kategorie-select"
                            >
                              {categories.map((c) => (
                                <option key={c.kategorie} value={c.kategorie}>{c.kategorie}</option>
                              ))}
                            </select>
                            <select
                              value={g.items[0]?.visibility || "nach_NDA"}
                              onChange={(e) => changeVisibilityAll(g.kategorie, e.target.value)}
                              className={`px-2 py-1 text-[11px] border border-navy/15 focus:outline-none focus:border-navy font-sans ${VIS_COLOR[g.items[0]?.visibility] || "bg-white text-navy"}`}
                              data-testid="folder-upload-visibility-select"
                            >
                              <option value="sofort">Sofort</option>
                              <option value="nach_NDA">Nach NDA</option>
                              <option value="nach_indikativ">Nach Gebot</option>
                              <option value="intern">Team-intern</option>
                            </select>
                          </div>
                        )}
                      </div>
                      {!collapsed && (
                        <ul className="divide-y divide-navy/5">
                          {g.items.map((it, idx) => {
                            const key = `${it.path}_${idx}`;
                            const prog = uploadProgress[key] || uploadProgress[`${it.path}_${items.filter((x) => x.included).indexOf(it)}`];
                            const Icon = fileIcon(it.path);
                            return (
                              <li
                                key={it.path}
                                data-testid="folder-upload-item"
                                className={`flex items-center gap-3 px-3 py-1.5 text-xs font-sans ${it.included ? "" : "opacity-40"}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={it.included}
                                  onChange={(e) => toggleItem(it.path, e.target.checked)}
                                  className="shrink-0 accent-navy"
                                  disabled={phase !== "preview"}
                                />
                                <Icon size={12} className="text-navy/50 shrink-0" />
                                <div className="min-w-0 flex-1">
                                  <div className="truncate text-navy" title={it.path}>{it.path.split("/").slice(-1)[0]}</div>
                                  <div className="text-[10px] text-navy/55 truncate" title={it.path}>{it.path}</div>
                                </div>
                                <div className="text-[10px] text-navy/55 tabular-nums shrink-0 w-16 text-right">
                                  {fmtSize(it.file?.size)}
                                </div>
                                {phase !== "preview" && prog && (
                                  <div className="shrink-0 w-24 flex items-center gap-1.5">
                                    {prog.status === "done" && <CheckCircle2 size={12} className="text-emerald-700" />}
                                    {prog.status === "error" && <AlertCircle size={12} className="text-rose-700" />}
                                    {(prog.status === "uploading" || prog.status === "assembling") && (
                                      <>
                                        <div className="flex-1 h-1 bg-navy/8">
                                          <div className="h-full bg-navy" style={{ width: `${prog.pct}%` }} />
                                        </div>
                                        <span className="text-[9px] text-navy/55 tabular-nums">{prog.pct}%</span>
                                      </>
                                    )}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-navy/10 px-6 py-3 flex justify-between items-center shrink-0">
          <div className="text-[11px] font-sans text-navy/55">
            {phase === "uploading" && (
              <>
                <Loader2 size={12} className="inline animate-spin mr-1" /> Upload läuft — {doneCount + failCount} / {totalIncluded} Dateien
              </>
            )}
            {phase === "done" && (
              <>
                <CheckCircle2 size={12} className="inline text-emerald-700 mr-1" /> Upload abgeschlossen — {doneCount} erfolgreich
                {failCount > 0 && <span className="text-rose-700">, {failCount} fehlgeschlagen</span>}
              </>
            )}
          </div>
          <div className="flex gap-2">
            {phase === "preview" && (
              <button
                onClick={() => { setPhase("idle"); setItems([]); }}
                className="pb-btn-ghost text-xs uppercase tracking-widish"
                data-testid="folder-upload-back"
              >
                Anderer Ordner
              </button>
            )}
            <button
              onClick={onClose}
              disabled={phase === "uploading"}
              className="pb-btn-ghost text-xs uppercase tracking-widish disabled:opacity-30"
              data-testid="folder-upload-cancel"
            >
              {phase === "done" ? "Schließen" : "Abbrechen"}
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}
